/**
 * SponsorsSidebar — right-rail ad column on the Browse page.
 *
 * Renders 4 PREMIUM slots (top) followed by 3 NON-PREMIUM slots (bottom).
 * Each ad card carries:
 *   • Multi-image carousel (fixed 8 s auto-rotation, hover-pause)
 *   • Sanitized rich-text (DOMPurify defense-in-depth)
 *   • Optional CTA link that wraps the whole card
 *
 * Empty slots render a subtle placeholder so admins can tell at-a-glance
 * how many spots are open. On mobile (<lg) the sidebar collapses under
 * the main results — we let the parent decide via CSS.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";

const IMAGE_ROTATION_MS = 8000;
// Vertical marquee for the non-premium column. Roughly 5 seconds per card
// so all 6 ads get equal airtime. Runs indefinitely via CSS animation with
// a duplicated list so the loop is seamless.
const NONPREMIUM_SECONDS_PER_CARD = 5;
// Premium cards are considerably taller than sponsor cards — give them more
// airtime so they feel deliberate, not frantic.
const PREMIUM_SECONDS_PER_CARD = 8;

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["b", "i", "u", "strong", "em", "a", "br", "p", "ul", "ol", "li", "span", "div", "h3", "h4", "s", "code"],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "style"],
};

export function sanitizeAdHtml(html) {
  if (!html) return "";
  const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);
  return clean.replace(/<a\s+([^>]*target=["']_blank["'][^>]*)>/gi, (_m, attrs) => {
    if (/rel\s*=/.test(attrs)) return `<a ${attrs}>`;
    return `<a ${attrs} rel="noopener noreferrer">`;
  });
}

function EmptySlot({ label, testId, slotKey }) {
  // slotKey e.g. "premium-2" or "nonpremium-4" gets carried to the inquiry
  // form so we can pre-fill the slot the prospect clicked on.
  return (
    <Link
      to={slotKey ? `/ad-inquiry?slot=${slotKey}` : "/ad-inquiry"}
      data-testid={testId}
      className="block border border-dashed border-white/10 hover:border-[#FF5F1F]/60 p-4 text-center transition-colors group"
    >
      <div className="font-mono text-[9px] tracking-widest uppercase text-zinc-600 group-hover:text-zinc-400">
        {label}
      </div>
      <div className="mt-1 text-[10px] text-zinc-700 italic group-hover:text-zinc-500">
        Available
      </div>
      <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-[#FFD700] group-hover:text-[#FF5F1F]">
        Click here to inquire →
      </div>
    </Link>
  );
}

function AdCarousel({ images, alt, big = false }) {
  const [idx, setIdx] = useState(0);
  const hover = useRef(false);
  const list = images && images.length ? images : [];

  useEffect(() => {
    if (list.length <= 1) return undefined;
    const t = setInterval(() => {
      if (hover.current) return;
      setIdx((i) => (i + 1) % list.length);
    }, IMAGE_ROTATION_MS);
    return () => clearInterval(t);
  }, [list.length]);

  if (!list.length) return null;

  // Premium ads use a modestly-tall 3:2 canvas so they occupy meaningful
  // screen real estate without dominating the rail; non-premium keep the
  // compact 16:10 so they stay unobtrusive.
  const aspect = big ? "3 / 2" : "16 / 10";

  return (
    <div
      className="relative w-full bg-black overflow-hidden"
      style={{ aspectRatio: aspect }}
      onMouseEnter={() => { hover.current = true; }}
      onMouseLeave={() => { hover.current = false; }}
    >
      {list.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={alt || "sponsor"}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          style={{ opacity: i === idx ? 1 : 0 }}
        />
      ))}
      {list.length > 1 && (
        <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1">
          {list.map((_, i) => (
            <span
              key={i}
              className="h-1 rounded-full transition-all duration-300"
              style={{
                width: i === idx ? 12 : 3,
                background: i === idx ? "#FF5F1F" : "rgba(255,255,255,0.4)",
                borderRadius: "9999px",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdCard({ ad, kind, index }) {
  const testId = `sidebar-ad-${kind}-${index + 1}`;
  const cleanHtml = useMemo(() => sanitizeAdHtml(ad?.content), [ad]);
  const big = kind === "premium";

  if (!ad) {
    return (
      <EmptySlot
        testId={testId}
        label={big ? `Premium Slot ${index + 1}` : `Sponsor Slot ${index + 1}`}
        slotKey={`${big ? "premium" : "nonpremium"}-${index + 1}`}
      />
    );
  }

  const hasLink = !!ad.link_url;
  const CardEl = hasLink ? "a" : "div";
  const cardProps = hasLink
    ? {
        href: ad.link_url,
        target: ad.link_new_tab ? "_blank" : undefined,
        rel: ad.link_new_tab ? "noopener noreferrer" : undefined,
      }
    : {};

  return (
    <CardEl
      data-testid={testId}
      {...cardProps}
      className={`block relative border ${big ? "border-2 border-[#FF5F1F]/60 shadow-[0_0_24px_rgba(255,95,31,0.15)]" : "border-white/10"} bg-[#0f0f0f] hover:border-white/30 transition-colors group`}
    >
      {big && (
        <div className="absolute bottom-1.5 right-1.5 z-10 font-mono text-[8px] tracking-widest uppercase bg-gradient-to-r from-[#FF5F1F] to-[#FFD700] text-black font-black px-1.5 py-0.5">
          ★ Premium
        </div>
      )}
      {!big && (
        <div className="absolute top-1.5 left-1.5 z-10 font-mono text-[8px] tracking-widest uppercase bg-white/10 text-zinc-300 px-1.5 py-0.5">
          Sponsor
        </div>
      )}

      {ad.images && ad.images.length > 0 && (
        <AdCarousel images={ad.images} alt={ad.title} big={big} />
      )}

      <div className={`${big ? "p-3" : "p-3"} ${(!ad.images || ad.images.length === 0) ? (big ? "pt-7" : "pt-7") : ""}`}>
        {ad.title && (
          <div
            className={`font-display font-black text-white leading-tight ${big ? "text-sm mb-1 line-clamp-2" : "text-sm mb-1 truncate"}`}
          >
            {ad.title}
          </div>
        )}
        {ad.owner_username && big && (
          <div className="font-mono text-[9px] text-zinc-400 mb-1">@{ad.owner_username}</div>
        )}
        {cleanHtml && (
          <div
            className={`h57-splash-body text-zinc-300 leading-snug ${big ? "text-[11px]" : "text-xs"}`}
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />
        )}
        {ad.link_url && (
          <div
            className={`font-mono tracking-widest uppercase text-[#FFD700] group-hover:text-[#FF5F1F] transition-colors ${big ? "mt-2 text-[10px] font-bold" : "mt-2 text-[10px]"}`}
          >
            Click Here →
          </div>
        )}
      </div>
    </CardEl>
  );
}

/**
 * Shared data hook — both rails hit the same endpoint. We fetch once from
 * the top-level and pass the data down (Browse mounts both rails).
 */
function useAdsData() {
  const [premium, setPremium] = useState([null, null, null, null]);
  const [nonpremium, setNonpremium] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ads/browse-sidebar");
        if (cancelled) return;
        setPremium((data.premium || []).slice(0, 4).concat(new Array(4).fill(null)).slice(0, 4));
        setNonpremium(data.nonpremium || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);
  return { premium, nonpremium, loaded };
}

/**
 * PremiumRail — right column, filled with premium/featured slots only.
 * Sticky (stays in the viewport while the user scrolls). Behaviour:
 *   • 1–2 filled ads → static, full-height, no marquee (they scroll with the window).
 *   • 3+ filled ads  → vertical marquee kicks in, sized to show ~2 full cards.
 * Empty premium slots are hidden entirely — premium inquiries go through /ad-inquiry.
 */
export function PremiumRail({ testId = "premium-rail" }) {
  const { premium, loaded } = useAdsData();
  const [paused, setPaused] = useState(false);

  const filled = useMemo(() => premium.filter(Boolean), [premium]);
  // Only marquee once we have MORE than 2 filled premium ads — at 1 or 2
  // the user should always see every ad in full, in a fixed position.
  const shouldMarquee = filled.length > 2;
  const durationSec = Math.max(filled.length, 1) * PREMIUM_SECONDS_PER_CARD * 2;

  if (!loaded) return null;
  if (filled.length === 0) return null;

  return (
    <aside
      data-testid={testId}
      className="w-full lg:w-[240px] shrink-0 lg:sticky lg:top-24 lg:self-start"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F] mb-2">→ Featured Sponsors</div>

      {shouldMarquee ? (
        <div
          data-testid={`${testId}-marquee`}
          className="relative overflow-hidden h57-ad-marquee-viewport"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          // ~2 full premium cards worth of vertical room (rail is ~180px wide
          // so cards are compact) — anything above scrolls in via marquee.
          style={{ maxHeight: "520px" }}
        >
          <div
            className="space-y-4"
            style={{
              animation: `h57AdMarquee ${durationSec}s linear infinite`,
              animationPlayState: paused ? "paused" : "running",
            }}
          >
            {filled.map((ad, i) => (
              <AdCard key={`p-a-${i}`} ad={ad} kind="premium" index={i} />
            ))}
            {filled.map((ad, i) => (
              <AdCard key={`p-b-${i}`} ad={ad} kind="premium" index={i} />
            ))}
          </div>
          <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-[#0a0a0a] to-transparent" />
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        </div>
      ) : (
        <div className="space-y-4" data-testid={`${testId}-static`}>
          {filled.map((ad, i) => (
            <AdCard key={`p-static-${i}`} ad={ad} kind="premium" index={i} />
          ))}
        </div>
      )}
    </aside>
  );
}

/**
 * SponsorsRail — left column, filled with the 6 regular/sponsor slots.
 * Sticky (stays in the viewport while the user scrolls) and continuously
 * marquees the filled ads vertically. Empty slots render statically above
 * the marquee so the "Click here to inquire" CTAs remain always visible.
 */
export function SponsorsRail({ testId = "sponsors-rail" }) {
  const { nonpremium, loaded } = useAdsData();
  const [paused, setPaused] = useState(false);

  const filled = useMemo(() => nonpremium.filter(Boolean), [nonpremium]);
  const emptyIndices = useMemo(
    () => nonpremium.map((v, i) => (v ? null : i)).filter((v) => v !== null),
    [nonpremium],
  );
  const durationSec = Math.max(filled.length, 1) * NONPREMIUM_SECONDS_PER_CARD * 2;
  const shouldMarquee = filled.length > 1;

  if (!loaded) return null;

  return (
    <aside
      data-testid={testId}
      className="w-full lg:w-[240px] shrink-0 lg:sticky lg:top-24 lg:self-start"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">→ Sponsors</div>

      {emptyIndices.length > 0 && (
        <div className="space-y-3 mb-3">
          {emptyIndices.map((i) => (
            <AdCard key={`empty-${i}`} ad={null} kind="nonpremium" index={i} />
          ))}
        </div>
      )}

      {filled.length > 0 && (
        <div
          data-testid={`${testId}-marquee`}
          className="relative overflow-hidden h57-ad-marquee-viewport"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          style={{ maxHeight: filled.length === 1 ? "auto" : "560px" }}
        >
          <div
            className="space-y-3"
            style={{
              animation: shouldMarquee ? `h57AdMarquee ${durationSec}s linear infinite` : "none",
              animationPlayState: paused ? "paused" : "running",
            }}
          >
            {filled.map((ad, i) => (
              <AdCard key={`n-a-${i}`} ad={ad} kind="nonpremium" index={i} />
            ))}
            {shouldMarquee && filled.map((ad, i) => (
              <AdCard key={`n-b-${i}`} ad={ad} kind="nonpremium" index={i} />
            ))}
          </div>
          {shouldMarquee && (
            <>
              <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-[#0a0a0a] to-transparent" />
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
            </>
          )}
        </div>
      )}
    </aside>
  );
}

/**
 * Legacy combined sidebar — kept as a default export so any older import
 * that still references it continues to work. Wraps both rails vertically
 * with premium first. New code should mount PremiumRail / SponsorsRail
 * explicitly on the sides of the layout.
 */
export default function SponsorsSidebar({ testId = "sponsors-sidebar" }) {
  return (
    <aside data-testid={testId} className="w-full lg:w-[320px] shrink-0 space-y-6">
      <PremiumRail testId={`${testId}-premium`} />
      <SponsorsRail testId={`${testId}-sponsors`} />
    </aside>
  );
}
